/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { CheckboxToggleMixin } from '../../utils/checkbox-toggle-mixin.js';
import { ControlStateMixin } from '../../utils/control-state-mixin.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { FocusBlurMixin } from '../../utils/focus-blur-mixin.js';
import { extendInteractiveArea } from '../../utils/interactive-area.js';
import checkboxStyles from './sh-checkbox.lit.scss.js';

/**
 * @prop {String} label - Defines the label of the checkbox.
 * @prop {Boolean} active - Defines if a checkbox is checked or not. Changes when clicked.
 * @prop {Boolean} neutral - If true, the active state will be gray instead of orange.
 * @prop {Boolean} disabled - Defines if a checkbox is disabled. If true, it won't allow clicks.
 * @prop {Boolean} indeterminate - Defines if a checkbox is indeterminate. If true, it will show a 'minus' icon in the checkbox when checked instead of the tick icon.
 * @fires checked - Dispatched when clicking on checkbox.
 */
export class SHCheckbox extends CheckboxToggleMixin(ControlStateMixin(FocusBlurMixin(LitElement))) {
  static get properties() {
    return {
      indeterminate: { type: Boolean, reflect: true },
      label: { type: String, reflect: true },
    };
  }

  static get styles() {
    return [sharedStyles, checkboxStyles, extendInteractiveArea()];
  }

  render() {
    return html`
      <input
        tabindex="-1"
        id="checkbox"
        type="checkbox"
        role="presentation"
        ?checked="${this.active}"
        ?active="${this.active}"
        ?disabled="${this.disabled}"
        ?aria-checked="${this.indeterminate ? 'mixed' : this.active}"
        ?indeterminate="${this.indeterminate}"
        value="${this.active}"
      />
      <span id="pseudoCheckbox" class="focus-item"></span>
      <span class="checkboxLabel">${this.label}</span>
    `;
  }

  constructor() {
    super();
    this.label = 'label';
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    const listOfProperties = ['label', 'indeterminate'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  firstUpdated() {
    super.firstUpdated();
    this.addEventListener('click', () => {
      dispatchCustomEvent(this, 'checked');
    });
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('role')) this.setAttribute('role', 'checkbox');
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
  }

  _spaceKeyAction() {
    super._spaceKeyAction();
    this.handleClick();
  }
}

customElements.define('sh-checkbox', SHCheckbox);
